import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig.js';

export type FeedbackVoteValue = 1 | -1;

export interface FeedbackContext {
  route: string;
  section?: string;
}

export interface FeedbackItem {
  id: string;
  text: string;
  authorId: string;
  authorUsername?: string;
  createdAt: Timestamp | null;
  context: FeedbackContext;
  score: number;
  upvoteCount: number;
  downvoteCount: number;
}

export interface FeedbackVote {
  value: FeedbackVoteValue;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

const FEEDBACK_COLLECTION = 'feedback';

async function resolveAuthorUsername(authorId: string): Promise<string | null> {
  try {
    const influencerSnap = await getDoc(doc(db, 'influencers', authorId));
    if (influencerSnap.exists()) {
      const data = influencerSnap.data();
      const username =
        data.username ||
        data.identity?.handle ||
        data.displayName ||
        data.identity?.displayName;
      if (typeof username === 'string' && username.trim()) {
        return username.trim();
      }
    }

    const userSnap = await getDoc(doc(db, 'users', authorId));
    if (userSnap.exists()) {
      const data = userSnap.data();
      const username =
        data.username ||
        data.handle ||
        data.displayName ||
        data.name;
      if (typeof username === 'string' && username.trim()) {
        return username.trim();
      }
    }
  } catch (error) {
    console.warn('Failed to resolve author username:', error);
  }
  return null;
}

export async function createFeedback(params: {
  text: string;
  authorId: string;
  context: FeedbackContext;
}): Promise<string> {
  const feedbackRef = collection(db, FEEDBACK_COLLECTION);
  const authorUsername = await resolveAuthorUsername(params.authorId);
  const payload = {
    text: params.text,
    authorId: params.authorId,
    ...(authorUsername ? { authorUsername } : {}),
    createdAt: serverTimestamp(),
    context: params.context,
    score: 0,
    upvoteCount: 0,
    downvoteCount: 0,
  };
  const docRef = await addDoc(feedbackRef, payload);
  return docRef.id;
}

export async function listFeedback(limitCount = 50): Promise<FeedbackItem[]> {
  const feedbackRef = collection(db, FEEDBACK_COLLECTION);
  const feedbackQuery = query(
    feedbackRef,
    orderBy('score', 'desc'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(feedbackQuery);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      text: data.text || '',
      authorId: data.authorId || '',
      authorUsername:
        typeof data.authorUsername === 'string' ? data.authorUsername : undefined,
      createdAt: data.createdAt || null,
      context: data.context || { route: '' },
      score: typeof data.score === 'number' ? data.score : 0,
      upvoteCount: typeof data.upvoteCount === 'number' ? data.upvoteCount : 0,
      downvoteCount: typeof data.downvoteCount === 'number' ? data.downvoteCount : 0,
    } as FeedbackItem;
  });
}

export async function getUserVote(
  feedbackId: string,
  userId: string
): Promise<FeedbackVote | null> {
  const voteRef = doc(db, FEEDBACK_COLLECTION, feedbackId, 'votes', userId);
  const voteSnap = await getDoc(voteRef);
  if (!voteSnap.exists()) {
    return null;
  }
  return voteSnap.data() as FeedbackVote;
}

export async function toggleVote(
  feedbackId: string,
  userId: string,
  desired: FeedbackVoteValue
): Promise<{
  score: number;
  upvoteCount: number;
  downvoteCount: number;
  userVote: FeedbackVoteValue | 0;
}> {
  const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);
  const voteRef = doc(db, FEEDBACK_COLLECTION, feedbackId, 'votes', userId);

  return runTransaction(db, async (transaction) => {
    if (desired !== 1 && desired !== -1) {
      throw new Error('Invalid vote value.');
    }

    const [feedbackSnap, voteSnap] = await Promise.all([
      transaction.get(feedbackRef),
      transaction.get(voteRef),
    ]);

    if (!feedbackSnap.exists()) {
      throw new Error('Feedback item not found.');
    }

    const data = feedbackSnap.data();
    let upvoteCount = typeof data.upvoteCount === 'number' ? data.upvoteCount : 0;
    let downvoteCount = typeof data.downvoteCount === 'number' ? data.downvoteCount : 0;
    let score = typeof data.score === 'number' ? data.score : 0;

    const rawVoteValue = voteSnap.exists() ? voteSnap.data()?.value : null;
    const existingVote =
      rawVoteValue === 1 || rawVoteValue === -1 ? rawVoteValue : null;

    let nextVote: FeedbackVoteValue | 0 = existingVote || 0;

    if (!existingVote) {
      if (desired === 1) {
        upvoteCount += 1;
        score += 1;
        nextVote = 1;
      } else {
        downvoteCount += 1;
        score -= 1;
        nextVote = -1;
      }
      transaction.set(voteRef, {
        value: desired,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else if (existingVote === desired) {
      if (desired === 1) {
        upvoteCount -= 1;
        score -= 1;
      } else {
        downvoteCount -= 1;
        score += 1;
      }
      nextVote = 0;
      transaction.delete(voteRef);
    } else {
      if (desired === 1) {
        downvoteCount -= 1;
        upvoteCount += 1;
        score += 2;
        nextVote = 1;
      } else {
        upvoteCount -= 1;
        downvoteCount += 1;
        score -= 2;
        nextVote = -1;
      }
      transaction.update(voteRef, {
        value: desired,
        updatedAt: serverTimestamp(),
      });
    }

    upvoteCount = Math.max(0, upvoteCount);
    downvoteCount = Math.max(0, downvoteCount);
    score = upvoteCount - downvoteCount;

    transaction.update(feedbackRef, {
      upvoteCount,
      downvoteCount,
      score,
    });

    return {
      upvoteCount,
      downvoteCount,
      score,
      userVote: nextVote,
    };
  });
}
